import { timezones } from "@/overlay/components/DatetimeSelectionWindow/TimeSettings/TimeZoneConst";
import styles from "@/overlay/components/QuickAccessLabel/LabelTitle.module.scss";
import {
  DATE_AND_TIME_FORMAT_WITH_WEEKDAY,
  normalizeDateString,
} from "@/overlay/components/TimeSettingsButtons/formats";
import { convertToCSSProperties } from "@/utility/style";
import { useLingui } from "@lingui/react";
import type { ElementStyle, GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime } from "luxon";
import { useMemo } from "react";

export function LabelDescription(props: {
  titleStyle?: ElementStyle;
  datetimeStart?: DateTime;
  datetimeEnd?: DateTime;
  timezone: GuiTimeZone;
  onClick?: () => void;
  disableCursor?: boolean;
  disabled?: boolean;
}) {
  const { datetimeStart, timezone, onClick, titleStyle, disableCursor = false, disabled } = props;
  const timeZoneFn = timezones[timezone as GuiTimeZone];
  const timeZoneLabel = typeof timeZoneFn === "function" ? timeZoneFn() : String(timezone);

  const cursorStyle = disableCursor ? "default" : "pointer";
  const { i18n } = useLingui();

  // Memoize the localized and formatted string
  const formattedDateTime = useMemo(() => {
    if (!datetimeStart) {
      return undefined;
    }

    const localizedDatetime = datetimeStart.setZone(timezone).setLocale(i18n.locale);
    return normalizeDateString(localizedDatetime.toLocaleString(DATE_AND_TIME_FORMAT_WITH_WEEKDAY));
  }, [datetimeStart, timezone, i18n.locale]);

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <span
        data-testid="datetime-label-formatted"
        className={styles["formatted-datetime"]}
        onClick={onClick}
        style={{
          ...convertToCSSProperties(titleStyle),
          cursor: cursorStyle,
          ...(disabled ? { pointerEvents: "none" } : {}),
        }}
      >
        {formattedDateTime || ""}
      </span>
      <span
        style={{
          ...convertToCSSProperties(titleStyle),
          cursor: cursorStyle,
          ...(disabled ? { pointerEvents: "none" } : {}),
        }}
      >
        {timeZoneLabel}
      </span>
    </>
  );
}
